/**
 * shared/date-nudge.ts — PRD §7 "cadence fit" + §9 re-time rule (card
 * yejoF8uTVxyf).
 *
 * The concierge's proactive date behaviour: a due-check that runs when the
 * Dates surface is READ (no couples index exists outside the Durable Objects,
 * so a Worker cron is off the table — repo rule), posting at most ONE Thread
 * nudge per window, addressed to the couple and never to one partner.
 *
 * Two rules live here, both pure so a probe can drive them with an overridden
 * clock instead of waiting for a real Thursday:
 *
 *   1. WINDOW — one nudge per local calendar month. The cadence target is
 *      "dates per month" (PRD §6.4), so the month is the only period that
 *      means anything: a couple is never nudged twice for the same pace it
 *      already failed to keep.
 *   2. SLOTS — a nudge only fires in a slot: Thursday evening (18:00 local,
 *      preferred) or Monday (the fallback, for the week whose Thursday
 *      evening passed). Every other day re-times to the next slot instead of
 *      nudging at random — "Thursday evening > Monday", never "whenever we
 *      happened to notice".
 *
 * Timezone: the caller sends its own `offsetMinutes`
 * (Date.getTimezoneOffset(), minutes to add to local time to reach UTC), so
 * "Thursday evening" is the couple's Thursday evening — the worker clock is
 * UTC and would otherwise nudge an AU couple on Friday morning.
 *
 * Nothing here reads the database or the network: inputs in, decision out.
 */
import { DEFAULT_DATE_PREFS } from './date-prefs.ts';

/** The agent-card payload marker Thread parses out of the text column. */
export const DATE_NUDGE_CARD = 'date-nudge';

export type DateNudgeSlot = 'thu-eve' | 'mon';

export type DateNudgeReason =
  /** A nudge was posted this call. */
  | 'sent'
  /** Cadence target already met for the period — nothing to say. */
  | 'on-pace'
  /** This window already had its nudge. */
  | 'already-nudged'
  /** Behind, but outside both slots — re-timed to nextSlotAt. */
  | 'waiting-for-slot'
  /** Not a couple yet (solo accumulation) — nudges target the pair. */
  | 'no-couple'
  /** The Thread write failed; the window was rolled back to retry. */
  | 'post-failed';

export interface DateNudgeCardPayload {
  v: 1;
  card: typeof DATE_NUDGE_CARD;
  /** Local 'YYYY-MM' the nudge belongs to — one card per window. */
  window: string;
  target: number;
  done: number;
  copy: string;
  postedAt: number;
}

export interface DateNudgeResult {
  sent: boolean;
  reason: DateNudgeReason;
  window: string;
  slot: DateNudgeSlot | null;
  /** Epoch ms the re-timed nudge becomes eligible, null when not waiting. */
  nextSlotAt: number | null;
  target: number;
  done: number;
  /** Couple-addressed copy; null unless the nudge actually went out. */
  copy: string | null;
}

/** getTimezoneOffset() range with margin: anything else is a bad client. */
function clampOffset(offsetMinutes: number): number {
  const n = Number.isFinite(offsetMinutes) ? Math.round(offsetMinutes) : 0;
  return Math.min(840, Math.max(-720, n));
}

export interface NudgeLocal {
  /** The moment expressed in the couple's local timeline (ms since epoch). */
  localMs: number;
  year: number;
  /** 1-12 */
  month: number;
  day: number;
  /** 0 = Sunday .. 6 = Saturday (Date#getUTCDay vocabulary). */
  weekday: number;
  hour: number;
  /** Local calendar month the cadence target is counted against. */
  window: string;
  /** Epoch ms the local month opened — the `done` counter's lower bound. */
  periodStart: number;
}

/**
 * Decompose `now` into the couple's local calendar. All the date maths runs
 * on the shifted value with UTC getters, so no locale, no timezone DB and no
 * server clock preference leaks into the rule.
 */
export function nudgeLocal(now: number, offsetMinutes: number): NudgeLocal {
  const off = clampOffset(offsetMinutes);
  const localMs = now - off * 60_000;
  const d = new Date(localMs);
  const year = d.getUTCFullYear();
  const month = d.getUTCMonth() + 1;
  return {
    localMs,
    year,
    month,
    day: d.getUTCDate(),
    weekday: d.getUTCDay(),
    hour: d.getUTCHours(),
    window: `${year}-${String(month).padStart(2, '0')}`,
    periodStart: Date.UTC(year, month - 1, 1) + off * 60_000,
  };
}

/** Which slot (if any) this moment sits in. See the module header. */
export function nudgeSlotAt(l: NudgeLocal): DateNudgeSlot | null {
  if (l.weekday === 4 && l.hour >= 18) return 'thu-eve'; // Thursday evening
  if (l.weekday === 1) return 'mon'; // Monday, all day
  return null;
}

/**
 * Epoch ms the re-timed nudge becomes eligible: the next slot opening after
 * `now` — Thursday 18:00 local or Monday 00:00 local, whichever comes first.
 * Called only when the moment is outside both slots, so the answer is always
 * in the future.
 */
export function nextNudgeSlotAt(now: number, offsetMinutes: number): number {
  const off = clampOffset(offsetMinutes);
  const l = nudgeLocal(now, off);
  if (nudgeSlotAt(l)) return now;
  const midnightToday = Date.UTC(l.year, l.month - 1, l.day);
  let best = Number.POSITIVE_INFINITY;
  for (let i = 0; i <= 8; i++) {
    const dayLocal = midnightToday + i * 86_400_000;
    const wd = new Date(dayLocal).getUTCDay();
    if (wd === 4) best = Math.min(best, dayLocal + 18 * 3_600_000 + off * 60_000);
    if (wd === 1) best = Math.min(best, dayLocal + off * 60_000);
  }
  const future = best > now ? best : now;
  return Number.isFinite(future) ? future : now;
}

/** PRD §6.4 cadence target bounds — the same 1-4 the prefs slider offers. */
export function clampNudgeTarget(target: number): number {
  const n = Number.isFinite(target) ? Math.round(target) : DEFAULT_DATE_PREFS.cadence;
  return Math.min(4, Math.max(1, n));
}

/**
 * The one line that goes out. Couple-addressed by construction — it names
 * neither partner, because a nudge targets the pair (PRD §9), and it never
 * blames: the pace is a number, not a score.
 */
export function dateNudgeCopy(done: number, target: number): string {
  const d = Math.max(0, Math.floor(done));
  const t = clampNudgeTarget(target);
  if (d <= 0) {
    return `You two have no date on the plan this month — we aim for ${t}. Set one up in Us → Dates.`;
  }
  const remaining = Math.max(0, t - d);
  return `You two have ${d} of ${t} dates this month — ${remaining} to go. Plan the next one in Us → Dates.`;
}

export interface DateNudgeInput {
  now: number;
  offsetMinutes: number;
  /** How many members the couple has — 1 means solo, no couple to nudge. */
  members: number;
  /** Cadence target for the period (intersection of both prefs, clamped). */
  target: number;
  /** Dates locked or completed so far in the local month. */
  done: number;
  /** The window a nudge already went out in, or null if never. */
  nudgeWindow: string | null;
}

/**
 * The whole rule, in one pass. Order matters: a couple on pace is told
 * nothing whatever the day, a couple already nudged this month is told
 * nothing whatever the day, and only then does the slot decide.
 */
export function decideDateNudge(input: DateNudgeInput): DateNudgeResult {
  const l = nudgeLocal(input.now, input.offsetMinutes);
  const slot = nudgeSlotAt(l);
  const target = clampNudgeTarget(input.target);
  const done = Math.max(0, Math.floor(input.done || 0));
  const base = { window: l.window, slot, nextSlotAt: null as number | null, target, done, copy: null as string | null };

  if (input.members < 2) return { sent: false, reason: 'no-couple', ...base };
  if (done >= target) return { sent: false, reason: 'on-pace', ...base };
  // `>=` rather than `===`: the stored window is the last one claimed, and a
  // stale clock (an older `now`, a device a month behind) must never undo a
  // newer claim and re-open the window it already spent.
  if (input.nudgeWindow && input.nudgeWindow >= l.window) {
    return { sent: false, reason: 'already-nudged', ...base };
  }
  if (!slot) {
    return { sent: false, reason: 'waiting-for-slot', ...base, nextSlotAt: nextNudgeSlotAt(input.now, input.offsetMinutes) };
  }
  return { sent: true, reason: 'sent', ...base, slot, nextSlotAt: input.now, copy: dateNudgeCopy(done, target) };
}

/** Parse the agent-card text Thread renders. Defensive like every other card
 *  parse: prose (the concierge's own cards share this kind) reads as null. */
export function parseDateNudge(raw: string): DateNudgeCardPayload | null {
  if (typeof raw !== 'string') return null;
  try {
    const p = JSON.parse(raw) as Record<string, unknown> | null;
    if (!p || typeof p !== 'object') return null;
    if (p.card !== DATE_NUDGE_CARD) return null;
    if (typeof p.copy !== 'string' || !p.copy) return null;
    if (typeof p.window !== 'string' || !/^\d{4}-\d{2}$/.test(p.window)) return null;
    return {
      v: 1,
      card: DATE_NUDGE_CARD,
      window: p.window,
      target: typeof p.target === 'number' ? p.target : 0,
      done: typeof p.done === 'number' ? p.done : 0,
      copy: p.copy,
      postedAt: typeof p.postedAt === 'number' ? p.postedAt : 0,
    };
  } catch {
    return null;
  }
}
